import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLegalAcceptance } from "../../context/LegalAcceptanceContext";
import "./LegalAcceptance.css";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function sortedDocuments(docs) {
  const order = { terms: 1, privacy: 2 };
  return (Array.isArray(docs) ? docs : [])
    .filter((doc) => doc.documentType === "terms" || doc.documentType === "privacy")
    .sort((a, b) => order[a.documentType] - order[b.documentType]);
}

export default function LegalAcceptanceModal() {
  const legal = useLegalAcceptance();
  const { status, submitting, submitError, acceptCurrentRequirements } = legal;
  const [checked, setChecked] = useState(false);
  const [localMessage, setLocalMessage] = useState("");
  const dialogRef = useRef(null);
  const titleRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const fingerprint = legal.requirementFingerprint;

  const docs = useMemo(
    () => sortedDocuments(status?.requiredDocuments),
    [status?.requiredDocuments]
  );

  useEffect(() => {
    setChecked(false);
    setLocalMessage("");
  }, [fingerprint]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (event.key !== "Tab") return;
    const dialog = dialogRef.current;
    const heading = titleRef.current;
    const focusable = Array.from(
      dialog?.querySelectorAll(FOCUSABLE_SELECTOR) || []
    ).filter((el) => !el.hasAttribute("disabled"));

    if (!focusable.length) {
      event.preventDefault();
      heading?.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    const focusIsOutsideDialog = dialog && active ? !dialog.contains(active) : true;

    if (active === heading || focusIsOutsideDialog) {
      event.preventDefault();
      if (event.shiftKey) last.focus();
      else first.focus();
      return;
    }

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    titleRef.current?.focus();
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.body.style.overflow = previousOverflow;
      const prior = previouslyFocusedRef.current;
      if (prior && prior.isConnected && typeof prior.focus === "function") {
        prior.focus();
      }
    };
  }, [handleKeyDown]);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!checked || submitting || !status?.canAccept || status?.isImpersonating) return;

    setLocalMessage("Submitting acceptance.");
    try {
      await acceptCurrentRequirements();
      setLocalMessage("Acceptance recorded.");
    } catch {
      setLocalMessage("");
    }
  }

  const disabled =
    !checked || submitting || !status?.canAccept || !!status?.isImpersonating;
  const arbitrationText = status?.copy?.arbitrationNoticeText;
  const errorMessage = submitError?.message || localMessage;

  return (
    <div className="legal-modal-backdrop">
      <form
        ref={dialogRef}
        className="legal-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-acceptance-title"
        aria-describedby="legal-acceptance-description"
        aria-busy={submitting ? "true" : "false"}
        onSubmit={handleSubmit}
      >
        {/* Clickwrap framing and button wording must be reviewed before enabling the rollout flag. */}
        <header className="legal-modal-header">
          <h1 id="legal-acceptance-title" ref={titleRef} tabIndex="-1">
            Review account requirements
          </h1>
          <p id="legal-acceptance-description">
            Review the current required documents before continuing.
          </p>
        </header>

        {arbitrationText ? (
          <p className="legal-arbitration-notice">{arbitrationText}</p>
        ) : null}

        <ol className="legal-document-list">
          {docs.map((doc) => (
            <li key={doc.documentType} className="legal-document-item">
              <div>
                <strong>{doc.title}</strong>
                <span>Version {doc.publicVersion}</span>
              </div>
              <div className="legal-document-links">
                <a href={doc.publicUrl} target="_blank" rel="noopener noreferrer">
                  View
                </a>
                {doc.pdfUrl ? (
                  <a href={doc.pdfUrl} target="_blank" rel="noopener noreferrer">
                    PDF
                  </a>
                ) : null}
              </div>
            </li>
          ))}
        </ol>

        <label className="legal-checkbox-row">
          <input
            type="checkbox"
            checked={checked}
            onChange={(event) => setChecked(event.target.checked)}
          />
          <span>{status?.copy?.checkboxText}</span>
        </label>

        <div className="legal-modal-footer">
          <div className="legal-live-region" aria-live="polite">
            {errorMessage || ""}
          </div>
          <button type="submit" className="legal-primary-button" disabled={disabled}>
            {submitting ? "Submitting" : "Continue"}
          </button>
        </div>
      </form>
    </div>
  );
}
